import { createAsyncThunk } from '@reduxjs/toolkit';
import { measurementsActions } from './measurementsSlice';
import { getMeasurementData } from '../../../TemporaryConmonents/Measurements/helpers/getMeasurementData';
import { openingCalculations } from '../../../TemporaryConmonents/Measurements/helpers/openingCalculations';
import { roundToThree } from '../../../TemporaryConmonents/Measurements/helpers/roundToThree';

export const fetchMeasurements = createAsyncThunk(
    'measurements/fetchMeasurement',
    async (_, thunkApi) => {
        const getMeasurementsUrl = routable.getRoute('listProjectMeasurement');

        try {
            const getMeasurementsResponse = await fetch(`${getMeasurementsUrl}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!getMeasurementsResponse.ok) {
                const error = await getMeasurementsResponse.json();
                loader.hide();
                swal({
                    text: error.message,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: i18next.t('swal.button.text.ok'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                }).then(function () {
                    window.location.reload();
                });
                return thunkApi.rejectWithValue(error.message);
            }

            const { data, is_last_page } = await getMeasurementsResponse.json();

            if (!is_last_page) {
                await thunkApi.dispatch(measurementsActions.changeNeedMore(!is_last_page));
            }

            if (!data) {
                throw new Error();
            }

            return data;
        } catch (e) {
            return thunkApi.rejectWithValue(error.message ? error.message : error.toString());
        }
    }
);

export const createMeasurement = createAsyncThunk(
    'measurements/createMeasurement',
    async (formData, thunkApi) => {
        const addMeasurementUrl = routable.getRoute('addMeasurement');

        try {
            const addMeasurementResponse = await fetch(`${addMeasurementUrl}`, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!addMeasurementResponse.ok) {
                const error = await addMeasurementResponse.json();
                loader.hide();
                swal({
                    text: error.message,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: i18next.t('swal.button.text.ok'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return thunkApi.rejectWithValue(error.message);
            }

            const { data } = await addMeasurementResponse.json();

            if (!data) {
                throw new Error();
            }

            return {
                id: data.id,
                name: data.name,
                floor_area: data.floor_area,
                perimeter: data.perimeter,
                wall_area: data.wall_area,
                preview_url: data.preview_url ? data.preview_url : null,
            };
        } catch (e) {
            return thunkApi.rejectWithValue(error.message ? error.message : error.toString());
        }
    }
);

export const loadMoreMeasurements = createAsyncThunk(
    'measurements/loadMoreMeasurements',
    async (_, thunkApi) => {
        const getMeasurementsUrl = routable.getRoute('listProjectMeasurement');

        const state = thunkApi.getState();

        const ids = await state.measurementsReducer.ids;

        const lastId = ids.slice(-1)[0];

        try {
            const loadMoreResponse = await fetch(`${getMeasurementsUrl}?last_id=${lastId}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!loadMoreResponse.ok) {
                const error = await loadMoreResponse.json();
                loader.hide();
                swal({
                    text: error.message,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: i18next.t('swal.button.text.ok'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                }).then(function () {
                    window.location.reload();
                });
                return thunkApi.rejectWithValue(error.message);
            }

            const { data, is_last_page } = await loadMoreResponse.json();

            if (is_last_page) {
                await thunkApi.dispatch(measurementsActions.changeNeedMore(!is_last_page));
            }

            return data;
        } catch (e) {
            return thunkApi.rejectWithValue(error.message ? error.message : error.toString());
        }
    }
);

export const getMeasurement = createAsyncThunk(
    'measurements/getMeasurement',
    async (measurementId, thunkApi) => {
        const getMeasurementsUrl = helpable.replaceUrlParameters(
            routable.getRoute('getMeasurement'),
            {
                _measurementId_: measurementId,
            }
        );

        try {
            const getMeasurementsResponse = await fetch(`${getMeasurementsUrl}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!getMeasurementsResponse.ok) {
                const error = await getMeasurementsResponse.json();
                loader.hide();
                swal({
                    text: error.message,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: i18next.t('swal.button.text.ok'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                }).then(function () {
                    window.location.reload();
                });
                return thunkApi.rejectWithValue(error.message);
            }

            const { data } = await getMeasurementsResponse.json();

            if (!data) {
                throw new Error();
            }

            const updatedOpenings = data.openings.map((opening) => {
                const updatedOpening = {
                    id: opening.id,
                    floor_distance: roundToThree(opening.floor_distance),
                    depth: roundToThree(opening.depth),
                    height: roundToThree(opening.length),
                    type: opening.type,
                    width: roundToThree(opening.width),
                };

                const { slope_area, slope_perimeter, area, perimeter } =
                    openingCalculations(updatedOpening);

                return { ...updatedOpening, slope_area, slope_perimeter, area, perimeter };
            });

            const updatedAdditionally = data.dimensions.map((additionally) => {
                return { ...additionally, value: roundToThree(additionally.value) };
            });

            const { calculations, manualCalculations } = getMeasurementData(data);

            await thunkApi.dispatch(measurementsActions.setIsManualCalculations(data.in_edit_mode));
            await thunkApi.dispatch(measurementsActions.addAttachments(data.attachments));
            await thunkApi.dispatch(measurementsActions.addManyOpenings(updatedOpenings));
            await thunkApi.dispatch(measurementsActions.addManyAdditionally(updatedAdditionally));
            await thunkApi.dispatch(measurementsActions.changeCalculations(calculations));
            await thunkApi.dispatch(
                measurementsActions.changeManualCalculations(manualCalculations)
            );
            await thunkApi.dispatch(measurementsActions.changeIsEdited(false));
        } catch (e) {
            return thunkApi.rejectWithValue(error.message ? error.message : error.toString());
        }
    }
);

export const removeMeasurement = createAsyncThunk(
    'measurements/removeMeasurement',
    async (measurementId, thunkApi) => {
        const removeMeasurementsUrl = helpable.replaceUrlParameters(
            routable.getRoute('removeMeasurement'),
            {
                _measurementId_: measurementId,
            }
        );

        try {
            const removeMeasurementResponse = await fetch(`${removeMeasurementsUrl}`, {
                method: 'DELETE',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!removeMeasurementResponse.ok) {
                const error = await removeMeasurementResponse.json();
                loader.hide();
                swal({
                    text: error.message,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: i18next.t('swal.button.text.ok'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return thunkApi.rejectWithValue(error.message);
            }

            return measurementId;
        } catch (e) {
            return thunkApi.rejectWithValue(error.message ? error.message : error.toString());
        }
    }
);

export const editMeasurement = createAsyncThunk(
    'measurements/editMeasurement',
    async ({ formData, measurementId }, thunkApi) => {
        const editMeasurementUrl = helpable.replaceUrlParameters(
            routable.getRoute('editMeasurement'),
            {
                _measurementId_: measurementId,
            }
        );

        try {
            const editMeasurementResponse = await fetch(`${editMeasurementUrl}`, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!editMeasurementResponse.ok) {
                const error = await editMeasurementResponse.json();
                loader.hide();
                swal({
                    text: error.message,
                    cancel: false,
                    confirm: true,
                    button: {
                        text: i18next.t('swal.button.text.ok'),
                        value: true,
                        visible: true,
                        className: 'btn btn-secondary',
                    },
                });
                return thunkApi.rejectWithValue(error.message);
            }

            const { data } = await editMeasurementResponse.json();

            if (!data) {
                throw new Error();
            }

            const editedMeasurement = {
                id: data.id,
                changes: {
                    id: data.id,
                    name: data.name,
                    floor_area: data.floor_area,
                    perimeter: data.perimeter,
                    wall_area: data.wall_area,
                    preview_url: data.preview_url ? data.preview_url : null,
                },
            };

            return editedMeasurement;
        } catch (e) {
            return thunkApi.rejectWithValue(error.message ? error.message : error.toString());
        }
    }
);
